import { HTMLChakraProps, useMergeRefs } from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { selectBrandIdx } from 'modules/Editor/store/editorSettingsSelector';
import { setBrandIdx } from 'modules/Editor/store/editorSettingsSlice';
import { useBrand } from 'modules/User';
import { DropdownPopover } from 'modules/common/components/ToolbarPopover';
import { DropdownPopoverProps, stopPropagationHandler } from 'modules/common/components/ToolbarPopover/DropdownPopover';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { parseFiniteNumberWithNaN } from 'utils/number';

export interface BrandOptionDropdownProps
  extends Omit<DropdownPopoverProps, 'label' | 'options' | 'selectedIndex' | 'onSelect'> {
  enableIfOnlyOneBrand?: boolean;
  contentClassName?: string;
  triggerClassName?: string;
}

const optionStyle = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as HTMLChakraProps<'div'>;

const sxStyle = {
  '& p': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

export const BrandOptionDropdown = ({
  enableIfOnlyOneBrand = false,
  triggerProps,
  contentClassName,
  triggerClassName,
  ...dropdownPopoverProps
}: BrandOptionDropdownProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('user', { keyPrefix: 'brand' });
  const { data: brandData } = useBrand();

  const internalTriggerRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useMergeRefs<HTMLButtonElement>(internalTriggerRef, triggerProps?.ref);

  const brandIdx = useAppSelector(selectBrandIdx);
  const onSetBrandIdx = useCallback(
    (value: string) => {
      const idx = parseFiniteNumberWithNaN(value);
      dispatch(setBrandIdx(idx));
    },
    [dispatch],
  );
  const brandNames = brandData?.map((brand, idx) => ({ value: idx, label: brand.name || 'My Brand' })) ?? [];

  // No need to check the dependency array as the handler has no dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleKeyPress = useCallback(stopPropagationHandler(internalTriggerRef), []);

  if (!brandNames.length || (brandNames.length === 1 && !enableIfOnlyOneBrand)) return null;

  return (
    <DropdownPopover
      ariaLabel={t('dropdown-label')}
      label=''
      useLabelInsteadOfSelectedLabel
      optionProps={optionStyle}
      containerProps={{ onKeyDown: handleKeyPress, onKeyUp: handleKeyPress, 'data-testid': 'brand-selector' }}
      triggerProps={{
        'data-testid': 'brand-dropdown-trigger',
        className: triggerClassName,
        ...triggerProps,
        ref: triggerRef,
        sx: sxStyle,
      }}
      closeOnSelect
      placement={'bottom-start'}
      returnFocusOnClose={false}
      selectedIndex={brandIdx}
      onSelect={onSetBrandIdx}
      options={brandNames}
      contentClassName={contentClassName}
      {...dropdownPopoverProps}
    />
  );
};
